import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    Typography,
    Card,
    CardContent,
    Button,
    Divider,
    Chip,
    Stack,
    Alert,
    CircularProgress,
} from "@mui/material";
import { Facebook, CheckCircle, ContentCopy } from "@mui/icons-material";
import { WB_SX, WB_UI } from "../../ui/ux/uiTokens";
import axiosInstance from "../../api/axiosInstance";
import base_url from "../../constants";
import useNotification from "../common/Notification";

// Meta / Facebook Embedded Signup configuration (from .env).
const FB_APP_ID = process.env.REACT_APP_FB_APP_ID;
const FB_CONFIG_ID = process.env.REACT_APP_FB_CONFIG_ID;
const FB_GRAPH_VERSION = process.env.REACT_APP_FB_GRAPH_VERSION || "v26.0";
const FB_SDK_SRC =
    process.env.REACT_APP_FB_SDK_SRC ||
    "https://connect.facebook.net/en_US/sdk.js";

const EmbeddedSignup = () => {
    const showNotification = useNotification();

    const [sdkReady, setSdkReady] = useState(false);
    const [initiating, setInitiating] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [sessionInfo, setSessionInfo] = useState(null); // { waba_id, phone_number_id }
    const [authCode, setAuthCode] = useState(null);
    const [backendDone, setBackendDone] = useState(false);
    const [configId, setConfigId] = useState(null);

    const configMissing = !FB_APP_ID || !FB_CONFIG_ID;

    // Keep the latest session info available inside FB callbacks without stale closures.
    const sessionInfoRef = useRef(null);
    const configIdRef = useRef(null);

    // 1. Load the Facebook JS SDK and initialize it.
    useEffect(() => {
        if (configMissing) {
            return;
        }

        window.fbAsyncInit = function () {
            if (window.FB) {
                window.FB.init({
                    appId: FB_APP_ID,
                    autoLogAppEvents: true,
                    xfbml: true,
                    version: FB_GRAPH_VERSION,
                });
                setSdkReady(true);
            }
        };

        // If the SDK is already present (e.g. navigating back to this page), re-init.
        if (window.FB) {
            window.fbAsyncInit();
            return;
        }

        if (!document.getElementById("facebook-jssdk")) {
            const script = document.createElement("script");
            script.id = "facebook-jssdk";
            script.async = true;
            script.defer = true;
            script.crossOrigin = "anonymous";
            script.src = FB_SDK_SRC;
            document.body.appendChild(script);
        }
    }, [configMissing]);

    // 2. Listen for the session info that Embedded Signup posts back via postMessage.
    useEffect(() => {
        const handleMessage = (event) => {
            if (
                event.origin !== "https://www.facebook.com" &&
                event.origin !== "https://web.facebook.com"
            ) {
                return;
            }

            try {
                const data =
                    typeof event.data === "string"
                        ? JSON.parse(event.data)
                        : event.data;

                if (data?.type === "WA_EMBEDDED_SIGNUP") {
                    if (data.event === "FINISH") {
                        const { phone_number_id, waba_id } = data.data || {};
                        const info = { waba_id, phone_number_id };
                        sessionInfoRef.current = info;
                        setSessionInfo(info);
                        showNotification(
                            "WhatsApp account details received.",
                            "info"
                        );
                    } else if (data.event === "CANCEL") {
                        showNotification(
                            `Signup cancelled at step: ${
                                data.data?.current_step || "unknown"
                            }`,
                            "warning"
                        );
                    } else if (data.event === "ERROR") {
                        showNotification(
                            `Signup error: ${
                                data.data?.error_message || "unknown error"
                            }`,
                            "error"
                        );
                    }
                }
            } catch (err) {
                // Non-JSON / unrelated messages are expected; ignore them.
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [showNotification]);

    // 4. Send the code (+ WABA/Phone IDs + config_id) to the backend for token exchange.
    const exchangeCodeOnBackend = useCallback(
        async (code) => {
            const info = sessionInfoRef.current || {};
            const activeConfigId = configIdRef.current;

            if (!activeConfigId) {
                showNotification(
                    "Signup session not found. Please start the flow again.",
                    "error"
                );
                return;
            }

            setSubmitting(true);
            try {
                await axiosInstance.post(
                    `${base_url}/whatsapp-business/embedded-signup`,
                    {
                        config_id: activeConfigId,
                        code,
                        waba_id: info.waba_id,
                        phone_number_id: info.phone_number_id,
                    }
                );
                setBackendDone(true);
                showNotification(
                    "Integration completed successfully.",
                    "success"
                );
            } catch (error) {
                showNotification(
                    error?.response?.data?.message ||
                        "Failed to complete integration on the server.",
                    "error"
                );
            } finally {
                setSubmitting(false);
            }
        },
        [showNotification]
    );

    // 3. Callback invoked when the user completes Facebook Login for Business.
    const fbLoginCallback = useCallback(
        (response) => {
            if (response.authResponse) {
                const code = response.authResponse.code;
                setAuthCode(code);
                exchangeCodeOnBackend(code);
            } else {
                showNotification(
                    "Facebook login was not completed.",
                    "warning"
                );
            }
        },
        [exchangeCodeOnBackend, showNotification]
    );

    // 5. Launch the Embedded Signup flow on button click.
    const launchWhatsAppSignup = useCallback(async () => {
        if (configMissing) {
            showNotification(
                "Facebook Embedded Signup env vars are not configured.",
                "error"
            );
            return;
        }
        if (!window.FB) {
            showNotification("Facebook SDK is still loading.", "warning");
            return;
        }

        setInitiating(true);
        setBackendDone(false);
        setAuthCode(null);
        setSessionInfo(null);
        setConfigId(null);
        sessionInfoRef.current = null;
        configIdRef.current = null;

        try {
            const response = await axiosInstance.post(
                `${base_url}/whatsapp-business/embedded-signup/initiate`
            );
            const initiatedConfigId = response?.data?.data?.config_id;

            if (!initiatedConfigId) {
                throw new Error("Missing config_id from initiate response.");
            }

            configIdRef.current = initiatedConfigId;
            setConfigId(initiatedConfigId);

            window.FB.login(fbLoginCallback, {
                config_id: FB_CONFIG_ID,
                response_type: "code",
                override_default_response_type: true,
                extras: { version: "v4" },
            });
        } catch (error) {
            showNotification(
                error?.response?.data?.message ||
                    "Failed to start embedded signup on the server.",
                "error"
            );
        } finally {
            setInitiating(false);
        }
    }, [configMissing, fbLoginCallback, showNotification]);

    const copyToClipboard = (value) => {
        if (!value) return;
        navigator.clipboard?.writeText(value);
        showNotification("Copied to clipboard.", "info");
    };

    return (
        <Box sx={WB_SX.page}>
            <Typography variant="h6" sx={WB_SX.pageTitle}>
                WhatsApp Embedded Signup
            </Typography>
            <Typography variant="body2" sx={{ ...WB_SX.pageSubtitle, mb: 3 }}>
                Onboard a WhatsApp Business Account using Meta's Embedded Signup
                flow. This is a test route for integration purposes.
            </Typography>

            {configMissing && (
                <Alert severity="error" sx={{ mb: WB_UI.spacing.sectionGap }}>
                    Facebook Embedded Signup is not configured. Set
                    REACT_APP_FB_APP_ID and REACT_APP_FB_CONFIG_ID in your .env
                    file, then restart the development server.
                </Alert>
            )}

            <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                <CardContent sx={WB_SX.cardContent}>
                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ mb: 2 }}
                    >
                        <Chip
                            size="small"
                            color={sdkReady ? "success" : "default"}
                            label={
                                configMissing
                                    ? "Config missing"
                                    : sdkReady
                                      ? "Facebook SDK ready"
                                      : "Loading Facebook SDK..."
                            }
                        />
                    </Stack>

                    <Button
                        variant="contained"
                        startIcon={
                            initiating || submitting ? (
                                <CircularProgress size={18} color="inherit" />
                            ) : (
                                <Facebook />
                            )
                        }
                        onClick={launchWhatsAppSignup}
                        disabled={
                            configMissing ||
                            !sdkReady ||
                            initiating ||
                            submitting
                        }
                        sx={{
                            backgroundColor: "#1877f2",
                            textTransform: "none",
                            fontWeight: 700,
                            height: 44,
                            px: 3,
                            borderRadius: 1,
                            "&:hover": { backgroundColor: "#166fe0" },
                        }}
                    >
                        Login with Facebook
                    </Button>
                </CardContent>
            </Card>

            {(configId || sessionInfo || authCode) && (
                <Card sx={{ mb: WB_UI.spacing.sectionGap, ...WB_SX.card }}>
                    <CardContent sx={WB_SX.cardContent}>
                        <Typography
                            variant="subtitle1"
                            sx={{ ...WB_SX.sectionTitle, mb: 2 }}
                        >
                            Session details
                        </Typography>

                        <Stack spacing={1.5}>
                            <DetailRow
                                label="Config ID"
                                value={configId}
                                onCopy={copyToClipboard}
                            />
                            <DetailRow
                                label="WABA ID"
                                value={sessionInfo?.waba_id}
                                onCopy={copyToClipboard}
                            />
                            <DetailRow
                                label="Phone Number ID"
                                value={sessionInfo?.phone_number_id}
                                onCopy={copyToClipboard}
                            />
                            <Divider />
                            <DetailRow
                                label="Auth Code"
                                value={authCode}
                                onCopy={copyToClipboard}
                            />
                        </Stack>

                        {backendDone && (
                            <Alert
                                icon={<CheckCircle fontSize="inherit" />}
                                severity="success"
                                sx={{ mt: 2 }}
                            >
                                Integration completed. Config status updated to
                                completed.
                            </Alert>
                        )}
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

const DetailRow = ({ label, value, onCopy }) => (
    <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
    >
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 140 }}>
            {label}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                fontFamily: "monospace",
                wordBreak: "break-all",
                flex: 1,
            }}
        >
            {value || "—"}
        </Typography>
        {value && (
            <Button
                size="small"
                startIcon={<ContentCopy fontSize="small" />}
                onClick={() => onCopy(value)}
                sx={{ textTransform: "none", flexShrink: 0 }}
            >
                Copy
            </Button>
        )}
    </Stack>
);

export default EmbeddedSignup;
